import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/Accordion/Accordion';
import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { Illustration } from '@/components/ui/Illustration/Illustration';
import { toast } from '@/components/ui/Toast/Toast';
import { paths } from '@/config/paths';
import { captureError } from '@/config/sentry';
import { getStudentEnrollmentDetails } from '@/features/scheduling/utils/getStudentEnrollmentDetails';
import { useTimeFor } from '@/hooks/useTimeFor';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useAuthStore } from '@/stores/authStore';
import { generateCalendarFile } from '@/utils/generateCalendarFile';
import { CalendarPlus, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { SessionSummary } from '../../../SessionSummary/SessionSummary';
import { useScheduleFlow } from '../../context/useScheduleFlow';

interface SuccessStepProps {
  onClose: () => void;
}

export const SuccessStep = ({ onClose }: SuccessStepProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog.steps.successStep' });
  const { t: tCalendar } = useTranslation('scheduling', { keyPrefix: 'addToCalendarData' });

  const { userData } = useAuthStore();
  const { formatDate } = useTimeFor('sessionTime');
  const { timeZone: calendarTimeZone } = useTimeFor('calendarExport');
  const students = userData?.students ?? [];

  const { data, goToStep, setErrorType } = useScheduleFlow();

  const notBookedAppointments = data.bookedAppointmentsDetails?.notBookedAppointments ?? [];

  const bookedAppointments = data.bookedAppointmentsDetails?.bookedAppointments ?? [];

  const hasBookedAppointments = bookedAppointments.length > 0;

  if (!hasBookedAppointments && notBookedAppointments.length === 1) {
    setErrorType('fullyBooked');
    goToStep('error');
    return;
  }

  const studentConfig = data.config.data?.students;

  if (!studentConfig) {
    throw new Error('Missing student session details');
  }

  const addToCalendar = () => {
    try {
      let appointment = bookedAppointments[0];
      for (const current of bookedAppointments) {
        if (current.duration_minutes > appointment.duration_minutes) {
          appointment = current;
        }
      }

      const { selectedStudentsIds } = data;
      const selectedStudents = students.filter((student) => selectedStudentsIds.includes(student.id));

      let title: string;
      let description: string;

      if (selectedStudents.length === 1) {
        const student = selectedStudents[0];

        const appointmentStudentConfig = data.config.data?.students.find((studentConfig) => studentConfig.id === appointment.student_id);
        const appointmentEnrollment = appointmentStudentConfig?.enrollments.find((enrollment) => enrollment.id === appointment?.enrollment_id);

        title = tCalendar('sessionTitle', {
          sessionName: appointmentEnrollment?.name,
          studentFirstName: student.firstName,
          studentLastName: student.lastName,
        });

        description = tCalendar('sessionDescription', {
          studentFirstName: student.firstName,
          studentLastName: student.lastName,
        });
      } else {
        const studentsNames = selectedStudents.map((student) => `${student.firstName} ${student.lastName}`).join(', ');

        title = tCalendar('sessionTitleMultiple', {
          studentsNames,
        });

        description = tCalendar('sessionDescriptionMultiple', {
          studentsNames,
        });
      }

      const location = userData?.center?.address ?? tCalendar('defaultLocation');

      const organizer = { name: userData?.center?.name, email: userData?.center?.email };

      void captureAnalyticsEvent('addtocalendar_clicked', {
        location: 'scheduling_success',
        appointments_ids: bookedAppointments.map((app) => app.appointment_id),
      });
      generateCalendarFile({ appointment, students, title, description, location, organizer, notBookedAppointments, timeZone: calendarTimeZone });
    } catch (error) {
      captureError(error);

      toast({
        description: tCalendar('addToCalendarErrorToast.description'),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <DialogBody>
        <div className='flex flex-1 flex-col items-center'>
          <Illustration name={hasBookedAppointments ? 'jumping' : 'problem'} className='m-3 w-[127px]' />
          <>
            <p className='text-heading-m-semibold mt-6 mb-4 w-[320px] max-w-full text-center'>{t(hasBookedAppointments ? 'title' : 'noSessionsBooked')}</p>
            <p className='text-text-default-secondary w-[320px] max-w-full text-center'>
              {t(hasBookedAppointments ? 'description' : 'descriptionNoSessionsBooked')}
            </p>
          </>
          {notBookedAppointments.length > 0 && (
            <Accordion type='single' collapsible className='mt-6 w-full'>
              <AccordionItem value='content' className='bg-fill-info-tile-orange text-body-m-regular w-full items-center rounded-lg px-5'>
                <AccordionTrigger>
                  <div className='flex items-center gap-2'>
                    <Info size={24} className='stroke-outline-icons-orange' strokeWidth={1.5} />
                    {t('notBookedAppointments.title', { count: notBookedAppointments.length })}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <p>{t('notBookedAppointments.description')}</p>
                  <ul className='mt-3'>
                    {notBookedAppointments.map((appointment) => (
                      <li key={appointment.date} className='ml-5 list-disc'>
                        <time dateTime={appointment.date}>{formatDate(appointment.date)}</time>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
          {hasBookedAppointments && (
            <div className='mt-4 w-full space-y-4'>
              {bookedAppointments.map((appointment) => {
                const enrollment = getStudentEnrollmentDetails(studentConfig, appointment.student_id, appointment.enrollment_id);

                return <SessionSummary key={appointment.appointment_id} appointment={appointment} enrollment={enrollment} className='w-full' />;
              })}
            </div>
          )}
        </div>
      </DialogBody>
      <DialogFooter className='flex-col! gap-3' withBorder={false}>
        {hasBookedAppointments && (
          <Button type='button' variant='text-secondary' onClick={addToCalendar} className='flex-1'>
            <CalendarPlus className='text-text-default-secondary size-5' />
            <span>{t('addToCalendarButton.label')}</span>
          </Button>
        )}
        <Button className='w-full' asChild onClick={onClose}>
          <Link to={paths.scheduling.sessions.home.path} className='flex items-center gap-2'>
            {t('sessionsButton.label')}
          </Link>
        </Button>
      </DialogFooter>
    </>
  );
};
